import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { APP_CONFIG } from '../config/app.config.constants';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  
  private googleAdsId: string = '';
  private measurementId: string = '';
  private metaPixelId: string = '';
  private gtmId: string = '';
  private utmParams: { [key: string]: string } = {};
  private gclid: string = '';

  constructor() {
    if (this.isBrowser) {
      this.initializeTracking();
      this.captureUTMParams();
      this.captureGclid();
      this.trackPageViews();
    }
  }

  /**
   * Initialize tracking scripts
   */
  private initializeTracking(): void {
    // Get tracking IDs from environment or APP_CONFIG
    const envTracking = (environment as any).tracking;
    this.googleAdsId = envTracking?.googleAdsId || APP_CONFIG.TRACKING.GOOGLE_ADS_ID || '';
    this.measurementId = envTracking?.measurementId || '';
    this.metaPixelId = envTracking?.metaPixelId || APP_CONFIG.TRACKING.META_PIXEL_ID || '';
    this.gtmId = envTracking?.googleTagManagerId || APP_CONFIG.TRACKING.GOOGLE_TAG_MANAGER_ID || '';

    // Google Ads gtag.js is loaded in index.html for production (with GA4 config)
    // Only load dynamically if not already present on the page
    if (this.googleAdsId && !window.gtag) {
      this.loadGoogleAds();
    }

    // Initialize Meta Pixel
    if (this.metaPixelId) {
      this.loadMetaPixel();
    }

    // Initialize Google Tag Manager
    if (this.gtmId) {
      this.loadGoogleTagManager();
    }
  }

  /**
   * Load Google Ads Global Site Tag (fallback if not loaded from index.html)
   */
  private loadGoogleAds(): void {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.googleAdsId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', this.googleAdsId, {
      send_page_view: false
    });

    // Also configure GA4 measurement ID so events flow to both destinations
    if (this.measurementId) {
      window.gtag('config', this.measurementId, {
        send_page_view: false
      });
    }
  }

  /**
   * Load Meta Pixel
   */
  private loadMetaPixel(): void {
    (function(f: any, b: any, e: string, v: string, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null, null, null);

    window.fbq('init', this.metaPixelId);
    window.fbq('track', 'PageView');
  }

  /**
   * Load Google Tag Manager
   */
  private loadGoogleTagManager(): void {
    (function(w: any, d: any, s: string, l: string, i: string) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s) as HTMLScriptElement;
      const dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode?.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', this.gtmId);
  }

  /**
   * Track page views
   */
  private trackPageViews(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackEvent('page_view', {
          page_path: event.urlAfterRedirects,
          page_title: document.title
        });
      });
  }

  /**
   * Capture UTM parameters from URL
   */
  private captureUTMParams(): void {
    if (!this.isBrowser) return;

    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    
    utmParams.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        this.utmParams[param] = value;
        // Store in sessionStorage for later use
        sessionStorage.setItem(param, value);
      } else {
        // Try to get from sessionStorage if not in URL
        const stored = sessionStorage.getItem(param);
        if (stored) {
          this.utmParams[param] = stored;
        }
      }
    });
  }

  /**
   * Capture Google Click ID (gclid) from URL for offline conversion uploads.
   * Stored in sessionStorage so it persists across page navigations.
   */
  private captureGclid(): void {
    if (!this.isBrowser) return;

    const urlParams = new URLSearchParams(window.location.search);
    const gclid = urlParams.get('gclid');
    if (gclid) {
      this.gclid = gclid;
      sessionStorage.setItem('gclid', gclid);
    } else {
      const stored = sessionStorage.getItem('gclid');
      if (stored) {
        this.gclid = stored;
      }
    }
  }

  /**
   * Get UTM parameters
   */
  getUTMParams(): { [key: string]: string } {
    return { ...this.utmParams };
  }

  /**
   * Get all tracking data for backend conversion uploads.
   * Includes gclid, UTM params, and landing page info.
   */
  getTrackingData(): { [key: string]: string } {
    const data: { [key: string]: string } = {};
    if (this.gclid) {
      data['gclid'] = this.gclid;
    }
    // Include UTM params
    Object.entries(this.utmParams).forEach(([key, value]) => {
      if (value) data[key] = value;
    });
    return data;
  }

  /**
   * Check if gtag is available (loaded from index.html or dynamically)
   */
  private hasGtag(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  /**
   * Track a conversion event with Google Ads conversion label support
   */
  trackConversion(eventName: string, value?: number, currency: string = 'INR', conversionLabel?: string): void {
    if (!this.isBrowser) return;

    const eventData: any = {
      event: eventName,
      ...this.utmParams
    };

    if (value !== undefined) {
      eventData.value = value;
      eventData.currency = currency;
    }

    // Google Ads conversion tracking
    if (this.hasGtag()) {
      const gtagData: any = {
        send_to: conversionLabel || this.googleAdsId,
        value: value,
        currency: currency,
        ...this.utmParams
      };
      window.gtag('event', 'conversion', gtagData);
      // Also fire as named event for analytics
      window.gtag('event', eventName, {
        value: value,
        currency: currency,
        ...this.utmParams
      });
    }

    // Meta Pixel conversion tracking (use standard events)
    if (this.metaPixelId && window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: eventName,
        value: value,
        currency: currency
      });
    }

    // Google Tag Manager dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData
      });
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, eventData?: any): void {
    if (!this.isBrowser) return;

    const data = {
      ...eventData,
      ...this.utmParams
    };

    // Google Ads / GA4 event tracking
    if (this.hasGtag()) {
      window.gtag('event', eventName, data);
    }

    // Meta Pixel custom event
    if (this.metaPixelId && window.fbq) {
      window.fbq('trackCustom', eventName, data);
    }

    // Google Tag Manager dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...data
      });
    }
  }

  /**
   * Track signup conversion with proper standard events
   */
  trackSignup(value?: number): void {
    const conversionLabel = APP_CONFIG.TRACKING.GOOGLE_ADS_CONVERSION_LABELS.SIGNUP || undefined;
    this.trackConversion(APP_CONFIG.TRACKING.CONVERSION_EVENTS.SIGNUP, value, 'INR', conversionLabel);

    // Meta Pixel: CompleteRegistration standard event
    if (this.metaPixelId && window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'signup',
        value: value,
        currency: 'INR'
      });
    }
  }

  /**
   * Track demo request conversion with proper standard events
   */
  trackDemoRequest(value?: number): void {
    const conversionLabel = APP_CONFIG.TRACKING.GOOGLE_ADS_CONVERSION_LABELS.DEMO_REQUEST || undefined;
    this.trackConversion(APP_CONFIG.TRACKING.CONVERSION_EVENTS.DEMO_REQUEST, value, 'INR', conversionLabel);

    // Meta Pixel: Schedule standard event
    if (this.metaPixelId && window.fbq) {
      window.fbq('track', 'Schedule', {
        content_name: 'demo_request',
        value: value,
        currency: 'INR'
      });
    }
  }

  /**
   * Track contact form submission conversion with proper standard events
   */
  trackContact(value?: number): void {
    const conversionLabel = APP_CONFIG.TRACKING.GOOGLE_ADS_CONVERSION_LABELS.CONTACT || undefined;
    this.trackConversion(APP_CONFIG.TRACKING.CONVERSION_EVENTS.CONTACT, value, 'INR', conversionLabel);

    // Meta Pixel: Contact standard event
    if (this.metaPixelId && window.fbq) {
      window.fbq('track', 'Contact', {
        content_name: 'contact_form'
      });
    }
  }

  /**
   * Track WhatsApp button clicks with location context
   */
  trackWhatsAppClick(location: string): void {
    this.trackEvent(APP_CONFIG.TRACKING.WHATSAPP_CLICK, {
      location: location
    });
  }

  /**
   * Track phone link clicks (tel:) with location context
   */
  trackPhoneClick(location: string): void {
    this.trackEvent(APP_CONFIG.TRACKING.PHONE_CLICK, {
      location: location
    });
  }

  /**
   * Track email link clicks (mailto:) with location context
   */
  trackEmailClick(location: string): void {
    this.trackEvent(APP_CONFIG.TRACKING.EMAIL_CLICK, {
      location: location
    });
  }

  /**
   * Track demo CTA button clicks with location context
   */
  trackDemoCTAClick(location: string): void {
    this.trackEvent(APP_CONFIG.TRACKING.DEMO_CTA_CLICK, {
      location: location
    });
  }

  /**
   * Track signup funnel step progression
   */
  trackSignupStep(step: number, stepName: string): void {
    if (!this.isBrowser) return;

    const eventData = {
      step_number: step,
      step_name: stepName,
      funnel: 'signup',
      ...this.utmParams
    };

    // Google Ads / GA4
    if (this.hasGtag()) {
      window.gtag('event', stepName, eventData);
    }

    // Meta Pixel: custom funnel event
    if (this.metaPixelId && window.fbq) {
      window.fbq('trackCustom', stepName, eventData);
    }

    // GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: stepName,
        ...eventData
      });
    }
  }

  /**
   * Track when a user starts interacting with a form
   */
  trackFormStart(formName: string): void {
    this.trackEvent(APP_CONFIG.TRACKING.FORM_EVENTS.FORM_START, {
      form_name: formName
    });
  }

  /**
   * Track form submission attempt (before API call)
   */
  trackFormSubmit(formName: string): void {
    this.trackEvent(APP_CONFIG.TRACKING.FORM_EVENTS.FORM_SUBMIT, {
      form_name: formName
    });
  }

  /**
   * Track form submission error
   */
  trackFormError(formName: string, errorMessage: string): void {
    this.trackEvent(APP_CONFIG.TRACKING.FORM_EVENTS.FORM_ERROR, {
      form_name: formName,
      error_message: errorMessage
    });
  }

  /**
   * Track page view
   */
  trackPageView(pagePath?: string, pageTitle?: string): void {
    if (!this.isBrowser) return;

    const path = pagePath || window.location.pathname;
    const title = pageTitle || document.title;

    // Google Ads / GA4 page view
    if (this.hasGtag()) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title
      });
    }

    // Meta Pixel page view
    if (this.metaPixelId && window.fbq) {
      window.fbq('track', 'PageView');
    }

    // Google Tag Manager page view
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: path,
        page_title: title,
        ...this.utmParams
      });
    }
  }
}
